import classnames from "classnames";
import * as React from "react";
import styles from "./Switcher.module.css";

export type SwitcherProps = {
  content: React.ReactNode;
  overlay: React.ReactNode;
  showOverlay: boolean;
};

const VISIBLE_CHILD = styles.child;
const HIDDEN_CHILD = `${styles.child} ${styles.hiddenChild}`;

/**
 * Layers two elements on top of each other, and hides one or
 * the other, never hiding or showing both at the same time.
 *
 * Whichever one should be visible initially should be set as `first`
 */
export const Switcher = React.memo(function Switcher(props: SwitcherProps) {
  /**
   * Store the two containers in refs in order to manipulate them via CSS
   */
  const contentContainer = React.useRef<HTMLDivElement | null>(null);
  const overlayContainer = React.useRef<HTMLDivElement | null>(null);

  const content = props.content;
  const overlay = props.overlay;

  const getChildClassNames = React.useCallback(
    (isVisible: boolean) => (isVisible ? VISIBLE_CHILD : HIDDEN_CHILD),
    [],
  );

  const getContainerClassNames = React.useCallback(
    () => ({
      contentClassName: getChildClassNames(!props.showOverlay),
      overlayClassName: getChildClassNames(props.showOverlay),
    }),
    [getChildClassNames, props.showOverlay],
  );

  React.useEffect((): void => {
    const { contentClassName, overlayClassName } = getContainerClassNames();

    if (contentContainer.current) {
      contentContainer.current.className = contentClassName;
    }

    if (overlayContainer.current) {
      overlayContainer.current.className = overlayClassName;
    }
  }, [getContainerClassNames]);

  const { contentClassName, overlayClassName } = React.useMemo(() => {
    if (!contentContainer.current && !overlayContainer.current) {
      return getContainerClassNames();
    }

    return {
      contentClassName: contentContainer.current?.className,
      overlayClassName: overlayContainer.current?.className,
    };
  }, [getContainerClassNames]);

  return (
    <div className={styles.container}>
      <div ref={contentContainer} className={contentClassName}>
        {content}
      </div>
      <div
        ref={overlayContainer}
        className={classnames(overlayClassName, styles.overlay)}
      >
        {overlay}
      </div>
    </div>
  );
});
